import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/api";
import { Brand, PoweredBy, Spinner } from "@/components/Brand";
import { Sparkles, FileText, MapPin, Download, Heart } from "lucide-react";
import { ProductGrid, downloadPdf } from "@/components/CatalogueModal";
import InterestTray from "@/components/InterestTray";
import { useInterests } from "@/hooks/useInterests";
import { useAuth } from "@/context/AuthContext";

export default function ShareCatalogue() {
  const { id, token } = useParams();
  const [cat, setCat] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [enq, setEnq] = useState({ name: "", phone: "", email: "", message: "", consent: false });
  const [enqSent, setEnqSent] = useState(false);
  const { role } = useAuth();
  const interest = useInterests(cat?.id, role === "visitor" && cat?.type === "ai");

  const sendEnquiry = async (e) => {
    e.preventDefault();
    const cid = cat?.id || id;
    try {
      await api.post(`/share/catalogue/${cid}/enquire`, { name: enq.name, phone: enq.phone, email: enq.email, message: enq.message });
      setEnqSent(true);
    } catch (_) { setEnqSent(true); }
  };

  useEffect(() => {
    const url = token ? `/share/by-token/${token}` : `/share/catalogue/${id}`;
    api.get(url)
      .then(({ data }) => setCat(data))
      .catch((err) => { if (err.response?.status === 401) { setNeedLogin(true); } else { setNotFound(true); } });
  }, [id, token]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="max-w-lg mx-auto w-full px-4 h-16 flex items-center justify-between">
        <Brand />
        <Link to="/" className="text-slate-400 hover:text-amber-300 text-sm" data-testid="share-home">Open App</Link>
      </header>

      <div className="max-w-lg mx-auto w-full px-4 flex-1 pb-16">
        {needLogin ? (
          <div className="glass rounded-3xl p-8 text-center mt-10" data-testid="share-login-required">
            <h1 className="font-display text-2xl text-slate-100">Log in to view this catalogue</h1>
            <p className="text-slate-400 text-sm mt-2">This collection is shared with registered JJS visitors.</p>
            <Link to="/visitor/login" className="inline-block btn-gold px-6 py-3 rounded-xl mt-5" data-testid="share-login-button">Visitor login</Link>
          </div>
        ) : notFound ? (
          <div className="glass rounded-3xl p-8 text-center mt-10" data-testid="share-notfound">
            <h1 className="font-display text-2xl text-slate-100">Catalogue unavailable</h1>
            <p className="text-slate-400 text-sm mt-2">This catalogue link is no longer active.</p>
          </div>
        ) : !cat ? (
          <Spinner />
        ) : (
          <div className="glass rounded-3xl p-6 mt-4 fade-up" data-testid="share-catalogue">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-amber-500/10 flex items-center justify-center shrink-0">
                {cat.type === "ai" ? <Sparkles size={22} className="text-amber-400" /> : <FileText size={22} className="text-amber-400" />}
              </div>
              <div>
                <h1 className="font-display text-2xl gold-text leading-tight">{cat.ai_content?.title || cat.title}</h1>
                <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5"><MapPin size={11} /> {cat.exhibitor_name}{cat.exhibitor_booth ? ` · ${cat.exhibitor_booth}` : ""}</div>
              </div>
            </div>

            {cat.type === "ai" ? (
              <>
                {cat.ai_content?.cover_image && <img src={`${API}/files/${cat.ai_content.cover_image}`} alt="" className="w-full aspect-[16/9] object-cover rounded-2xl mt-4" data-testid="share-cover" />}
                {cat.ai_content?.intro && <p className="text-slate-300 mt-4 leading-relaxed">{cat.ai_content.intro}</p>}
                <div className="flex items-center justify-between gap-2 mt-3">
                  <p className="text-[11px] text-slate-500 flex items-center gap-1"><Heart size={11} /> {interest ? "Tap the heart or double-tap a photo to select pieces." : <Link to="/visitor/login" className="underline" data-testid="share-login-to-like">Log in as a visitor to select pieces you like</Link>}</p>
                  <button onClick={() => downloadPdf(`/share/by-token/${cat.share_token}/pdf`, cat.title)} className="text-[11px] text-amber-300 flex items-center gap-1 shrink-0" data-testid="share-pdf-download"><Download size={12} /> PDF</button>
                </div>
                <ProductGrid products={cat.ai_content?.products || []} prefix="share" interest={interest} />
                {interest && <InterestTray interest={interest} exhibitorName={cat.exhibitor_name} />}
              </>
            ) : (
              <div className="mt-6 text-center">
                <p className="text-slate-400 text-sm">PDF catalogue from {cat.exhibitor_name}</p>
                {cat.storage_path && (
                  <a href={`${API}/files/${cat.storage_path}`} target="_blank" rel="noreferrer" className="inline-block btn-gold px-6 py-3 rounded-xl mt-4" data-testid="share-pdf-open">
                    Open PDF Catalogue
                  </a>
                )}
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-amber-500/10">
              <div className="text-sm font-semibold text-slate-100 mb-2">Enquire about this collection</div>
              {enqSent ? (
                <p className="text-sm text-emerald-300" data-testid="enquiry-sent">Thank you! The exhibitor will reach out to you.</p>
              ) : (
                <form onSubmit={sendEnquiry} className="space-y-2" data-testid="enquiry-form">
                  <input value={enq.name} onChange={(e) => setEnq({ ...enq, name: e.target.value })} placeholder="Your name" required className="w-full bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="enquiry-name" />
                  <div className="grid grid-cols-2 gap-2">
                    <input value={enq.phone} onChange={(e) => setEnq({ ...enq, phone: e.target.value })} placeholder="Phone" className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="enquiry-phone" />
                    <input value={enq.email} onChange={(e) => setEnq({ ...enq, email: e.target.value })} placeholder="Email" className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="enquiry-email" />
                  </div>
                  <textarea value={enq.message} onChange={(e) => setEnq({ ...enq, message: e.target.value })} placeholder="Message (optional)" rows={2} className="w-full bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="enquiry-message" />
                  <label className="flex items-start gap-2 text-[11px] text-slate-500"><input type="checkbox" checked={enq.consent} onChange={(e) => setEnq({ ...enq, consent: e.target.checked })} className="mt-0.5 accent-[var(--jjs-gold)]" />I consent to this exhibitor contacting me about my enquiry.</label>
                  <button disabled={!enq.consent} className="w-full btn-gold py-2.5 rounded-xl disabled:opacity-50" data-testid="enquiry-submit">Send Enquiry</button>
                </form>
              )}
            </div>

            <Link to="/visitor/login" className="block w-full glass py-3 rounded-xl mt-6 text-center text-slate-200 hover:text-amber-300 transition" data-testid="share-cta">
              Explore more at JJS Exhibition
            </Link>
          </div>
        )}
        <div className="mt-8"><PoweredBy /></div>
      </div>
    </div>
  );
}
